const ANON_BIN_KEY = "jhonks_anonymous_bin_v1";

const getEmptyBin = () => ({
  materials: [],
  totalPrice: 0,
  totalQuantity: 0,
  validationStatus: false,
  selectedAgent: null,
});

const computeTotals = (items) => {
  let totalQuantity = 0;
  let totalPrice = 0;

  for (const item of items) {
    const qty = Number(item.quantity || 0);
    const pricePerKg = Number(item.material?.pricePerKg || 0);
    totalQuantity += qty;
    totalPrice += qty * pricePerKg;
  }

  return { totalQuantity, totalPrice };
};

const loadRaw = () => {
  try {
    const raw = localStorage.getItem(ANON_BIN_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    return parsed;
  } catch {
    return { items: [] };
  }
};

const notifyAnonymousBinChanged = () => {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent("jhonks-anonymous-bin-changed"));
  }
};

const saveRaw = (raw) => {
  localStorage.setItem(ANON_BIN_KEY, JSON.stringify(raw));
  notifyAnonymousBinChanged();
};

const normalizeMaterialSnapshot = (material) => {
  if (!material) return null;
  const pricePerKg =
    material.pricePerKg ?? material.price_per_kg ?? material.price_per_kg ?? 0;

  return {
    _id: material._id,
    name: material.name,
    image: material.image,
    category: material.category,
    pricePerKg: Number(pricePerKg),
  };
};

const upsertItem = (items, materialId, materialSnapshot, quantity) => {
  const idx = items.findIndex((it) => String(it.materialId) === String(materialId));
  if (idx >= 0) {
    const nextQty = Number(items[idx].quantity || 0) + Number(quantity || 0);
    items[idx] = {
      ...items[idx],
      quantity: nextQty,
      material: materialSnapshot,
    };
    return items;
  }

  items.push({
    // We use materialId as the "_id" for local bin entries so the Bin UI can keep using item._id.
    _id: materialId,
    materialId,
    quantity: Number(quantity || 0),
    material: materialSnapshot,
  });
  return items;
};

export const anonymousBinService = {
  hasAnonymousBin: () => {
    const raw = loadRaw();
    return (raw.items || []).length > 0;
  },

  getBin: () => {
    const { items } = loadRaw();
    const normalizedItems = items
      .filter((it) => it && it.materialId && Number(it.quantity || 0) > 0)
      .map((it) => ({
        _id: it._id ?? it.materialId,
        material: {
          ...it.material,
          _id: it.materialId,
        },
        quantity: Number(it.quantity || 0),
        price: Number(it.quantity || 0) * Number(it.material?.pricePerKg || 0),
      }));

    const { totalQuantity, totalPrice } = computeTotals(normalizedItems);

    return {
      ...getEmptyBin(),
      materials: normalizedItems,
      totalQuantity,
      totalPrice,
    };
  },

  addToBin: ({ material, quantity }) => {
    const { items } = loadRaw();
    const materialSnapshot = normalizeMaterialSnapshot(material);
    if (!materialSnapshot?._id) return getEmptyBin();

    const nextItems = upsertItem(
      items,
      materialSnapshot._id,
      materialSnapshot,
      Number(quantity || 0)
    );

    saveRaw({ items: nextItems });
    return anonymousBinService.getBin();
  },

  updateQuantity: (itemId, newQuantity) => {
    const { items } = loadRaw();
    const materialId = itemId;

    const next = items
      .map((it) => {
        if (String(it.materialId) !== String(materialId)) return it;
        return { ...it, quantity: Number(newQuantity || 0) };
      })
      .filter((it) => Number(it.quantity || 0) > 0);

    saveRaw({ items: next });
    return anonymousBinService.getBin();
  },

  removeFromBin: (materialId) => {
    const { items } = loadRaw();
    const next = items.filter((it) => String(it.materialId) !== String(materialId));
    saveRaw({ items: next });
    return anonymousBinService.getBin();
  },

  clearBin: () => {
    localStorage.removeItem(ANON_BIN_KEY);
    notifyAnonymousBinChanged();
  },

  getRawItems: () => {
    const { items } = loadRaw();
    return (items || [])
      .filter((it) => it && it.materialId && Number(it.quantity || 0) > 0)
      .map((it) => ({
        materialId: String(it.materialId),
        quantity: Number(it.quantity || 0),
      }));
  },
};

